/**
 * AudioEngine — the bus, and everything that has to be synthesised rather than loaded.
 *
 * No audio files, for the same reason there are no meshes: it all comes out of a seed. Noise
 * is generated into a buffer, and the reverb's impulse response is generated too — a decaying
 * burst of noise IS an impulse response, so a real convolution reverb costs one buffer and no
 * download.
 *
 * THE SIGNAL PATH
 *
 *     music ─┐                    ┌─ dry ──┐
 *            ├─→ [ muffle ] ──────┤        ├─→ [ master ] ─→ out
 *     ambi ──┘    lowpass         └─ [ verb ] ─┘
 *
 * The muffle is the one piece doing real work: a lowpass whose corner falls as the shark
 * dives. Water absorbs high frequencies with depth, so the whole soundscape darkens as you go
 * down and opens up as you surface. It is the cheapest possible cue for where you are in the
 * water column and it costs one BiquadFilter.
 *
 * BROWSERS WILL NOT LET YOU START
 * An AudioContext begins suspended until a real user gesture. Everything here is built up
 * front and simply runs silent until unlock() is called from a click or a keypress.
 */

/* The tuned balance between the two buses. The player's sliders scale these; nothing else
   should ever write music.gain or ambience.gain directly. */
const MIX_MUSIC = 0.5;
const MIX_AMBIENCE = 1.0;
/** setTargetAtTime time constant for every mixer move — long enough that no step clicks */
const RAMP = 0.08;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export class AudioEngine {
  readonly ctx: AudioContext;
  /** everything musical goes here */
  readonly music: GainNode;
  /** everything environmental goes here */
  readonly ambience: GainNode;
  /** post-muffle send, for anything that wants to bypass the depth filter */
  readonly master: GainNode;

  private readonly muffle: BiquadFilterNode;
  private readonly wet: GainNode;
  private readonly dry: GainNode;
  private readonly noise: AudioBuffer;
  private unlocked = false;
  private keepAwake: HTMLAudioElement | null = null;
  private musicVol = 1;
  private sfxVol = 1;
  private musicOn = true;
  private sfxOn = true;

  constructor(volume = 0.55) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new Ctor({ latencyHint: 'playback' });
    const now = this.ctx.currentTime;

    this.master = this.ctx.createGain();
    this.master.gain.value = volume;
    this.master.connect(this.ctx.destination);

    /* Reverb. The impulse is noise under an exponential decay — that is what a room's
       response actually looks like, so it convolves into a perfectly serviceable hall. The
       stereo channels are decorrelated (independent noise), which is what makes it wide
       rather than a mono wash sitting in the middle of the head. */
    const verb = this.ctx.createConvolver();
    verb.buffer = this.impulse(3.6, 2.4);
    this.wet = this.ctx.createGain();
    this.wet.gain.value = 0.42;
    this.dry = this.ctx.createGain();
    this.dry.gain.value = 0.72;
    verb.connect(this.wet).connect(this.master);
    this.dry.connect(this.master);

    this.muffle = this.ctx.createBiquadFilter();
    this.muffle.type = 'lowpass';
    this.muffle.frequency.setValueAtTime(9000, now);
    this.muffle.Q.value = 0.6;
    this.muffle.connect(this.dry);
    this.muffle.connect(verb);

    this.music = this.ctx.createGain();
    this.music.gain.value = MIX_MUSIC;
    this.music.connect(this.muffle);

    this.ambience = this.ctx.createGain();
    this.ambience.gain.value = MIX_AMBIENCE;
    this.ambience.connect(this.muffle);

    this.noise = this.makeNoise(4.0);
  }

  /** true once a user gesture has let the context run */
  get running(): boolean {
    return this.unlocked && this.ctx.state === 'running';
  }

  /**
   * Call from a real user gesture. Safe to call repeatedly.
   *
   * ASYNC, and that was a bug rather than a style preference. resume() returns a promise, and
   * the old version checked ctx.state on the very next line — which on a phone is still
   * 'suspended', because the transition happens when the promise settles. So it reported
   * failure, the caller never started the scheduler, and audio only appeared on the SECOND
   * gesture (when the early-out for `unlocked` returned true). On a desktop resume tends to
   * land fast enough to hide it.
   */
  async unlock(): Promise<boolean> {
    this.unlocked = true;
    this.wakeMediaSession();
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch {
        /* refused; the caller decides whether that is worth saying */
      }
    }
    return this.ctx.state === 'running';
  }

  /** what the browser thinks: 'running', 'suspended' or 'closed'. For the settings panel. */
  get state(): AudioContextState {
    return this.ctx.state;
  }

  /**
   * The silent-switch workaround, and it is the reason audio can be dead on an iPhone that is
   * otherwise working perfectly.
   *
   * iOS puts a page's Web Audio in the "ambient" session category, which the hardware mute
   * switch silences — so a phone on silent plays nothing, with the context in state 'running'
   * and every gain node exactly where it should be. There is nothing to debug because nothing
   * is wrong. Playing an HTMLAudioElement moves the session to "playback", which the switch
   * does not govern, and a looping silent file keeps it there for the session.
   *
   * Costs one decoded tenth of a second of silence.
   */
  private wakeMediaSession(): void {
    if (this.keepAwake !== null) return;
    try {
      const el = new Audio(silentWavUrl());
      el.loop = true;
      // Or iOS opens the fullscreen player over the game.
      el.setAttribute('playsinline', '');
      el.volume = 1;
      void el.play().catch(() => { /* refused, and the game is no worse off */ });
      this.keepAwake = el;
    } catch {
      this.keepAwake = null;
    }
  }

  /* ---- the mixer the settings panel drives -----------------------------------------------
     Player volume MULTIPLIES the tuned bus level rather than replacing it. Music sits at 0.5
     against ambience at 1.0 because that is the balance the soundscape was written to; a
     slider that wrote gain directly would throw that away the moment anyone touched it, and
     100% would mean "music twice as loud as intended" instead of "music as intended".

     Every change ramps. Setting an AudioParam outright puts a step discontinuity in the
     signal, which is a click — audible, and worst on exactly the low sustained pads this game
     is mostly made of. */
  setMusicVolume(v: number): void {
    this.musicVol = clamp01(v);
    this.applyMusic();
  }

  setSfxVolume(v: number): void {
    this.sfxVol = clamp01(v);
    this.applySfx();
  }

  setMusicEnabled(on: boolean): void {
    this.musicOn = on;
    this.applyMusic();
  }

  setSfxEnabled(on: boolean): void {
    this.sfxOn = on;
    this.applySfx();
  }

  get musicVolume(): number { return this.musicVol; }
  get sfxVolume(): number { return this.sfxVol; }
  get musicEnabled(): boolean { return this.musicOn; }
  get sfxEnabled(): boolean { return this.sfxOn; }

  private applyMusic(): void {
    const g = this.musicOn ? MIX_MUSIC * this.musicVol : 0;
    this.music.gain.setTargetAtTime(g, this.ctx.currentTime, RAMP);
  }

  private applySfx(): void {
    const g = this.sfxOn ? MIX_AMBIENCE * this.sfxVol : 0;
    this.ambience.gain.setTargetAtTime(g, this.ctx.currentTime, RAMP);
  }

  /**
   * @param submerged 0 at the surface, 1 deep. Drives the muffle.
   */
  setDepth(submerged: number): void {
    const t = Math.max(0, Math.min(1, submerged));
    // 9 kHz at the surface down to 700 Hz deep. Exponential because pitch is logarithmic —
    // a linear sweep spends almost all its travel in the top octave, where nothing happens.
    const hz = 9000 * Math.pow(700 / 9000, t);
    this.muffle.frequency.setTargetAtTime(hz, this.ctx.currentTime, 0.35);
  }

  /** a looping source of the shared noise buffer, at a random offset so layers decorrelate */
  noiseSource(): AudioBufferSourceNode {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    return src;
  }

  /** brown-ish noise: integrated white, which is the spectrum moving water actually has */
  private makeNoise(seconds: number): AudioBuffer {
    const n = Math.floor(this.ctx.sampleRate * seconds);
    const buf = this.ctx.createBuffer(2, n, this.ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      let last = 0;
      for (let i = 0; i < n; i++) {
        const white = Math.random() * 2 - 1;
        last = (last + 0.02 * white) / 1.02;
        d[i] = last * 3.5;
      }
      /* Crossfade the tail into the head so the loop has no seam. A brown-noise loop with a
         hard join clicks once a cycle, and a periodic click is the single most noticeable
         artefact there is — the ear locks onto it within seconds. */
      const fade = Math.min(n >> 2, Math.floor(this.ctx.sampleRate * 0.25));
      for (let i = 0; i < fade; i++) {
        const t = i / fade;
        d[i] = d[i] * t + d[n - fade + i] * (1 - t);
      }
    }
    return buf;
  }

  /** noise under an exponential decay — the shape of a real room's impulse response */
  private impulse(seconds: number, decay: number): AudioBuffer {
    const n = Math.floor(this.ctx.sampleRate * seconds);
    const buf = this.ctx.createBuffer(2, n, this.ctx.sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      for (let i = 0; i < n; i++) {
        d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / n, decay);
      }
    }
    return buf;
  }

  dispose(): void {
    if (this.keepAwake) {
      this.keepAwake.pause();
      this.keepAwake.src = '';
      this.keepAwake = null;
    }
    void this.ctx.close();
  }
}

/**
 * A tenth of a second of silence, as a WAV, built rather than pasted.
 *
 * A base64 blob would be shorter and completely opaque; forty-four bytes of header and a run of
 * zero-level samples is at least readable by anyone wondering what it is and why it exists.
 * 8 kHz, mono, 8-bit — the smallest thing every browser will decode. Unsigned 8-bit PCM has
 * silence at 128, not 0.
 */
let silentUrl = '';
function silentWavUrl(): string {
  if (silentUrl !== '') return silentUrl;
  const rate = 8000;
  const samples = rate / 10;
  const buf = new ArrayBuffer(44 + samples);
  const v = new DataView(buf);
  const tag = (at: number, s: string): void => {
    for (let i = 0; i < s.length; i++) v.setUint8(at + i, s.charCodeAt(i));
  };
  tag(0, 'RIFF');
  v.setUint32(4, 36 + samples, true);
  tag(8, 'WAVEfmt ');
  v.setUint32(16, 16, true);          // PCM header length
  v.setUint16(20, 1, true);           // format: PCM
  v.setUint16(22, 1, true);           // channels
  v.setUint32(24, rate, true);
  v.setUint32(28, rate, true);        // byte rate = rate * channels * bytes
  v.setUint16(32, 1, true);           // block align
  v.setUint16(34, 8, true);           // bits per sample
  tag(36, 'data');
  v.setUint32(40, samples, true);
  for (let i = 0; i < samples; i++) v.setUint8(44 + i, 128);
  silentUrl = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }));
  return silentUrl;
}
